import React from 'react';
import { node, string, arrayOf, shape, bool } from 'prop-types';
import classNames from 'classnames';
import { Page, DashboardWrapper, NamedLink } from '../../components';

import css from './SettingsLayout.module.css';

/**
 * Shared chrome for any Settings page (Creator, Org, etc.).
 *
 * Renders the dashboard wrapper, a page title, optional subtitle, and a
 * horizontal tab nav. Tab content is supplied as children. The tab config
 * shape matches LinkTabNavHorizontal: { text, selected, linkProps }.
 */
const SettingsLayout = props => {
  const {
    className,
    rootClassName,
    currentPage,
    title,
    subtitle,
    tabs,
    pageTitle,
    scrollingDisabled,
    orgId,
    children,
  } = props;

  const classes = classNames(rootClassName || css.root, className);

  return (
    <Page className={classes} scrollingDisabled={scrollingDisabled} title={pageTitle || title}>
      <DashboardWrapper currentPage={currentPage} orgId={orgId} contentPadding>
        <div className={css.content}>
          <header className={css.header}>
            <h1 className={css.title}>{title}</h1>
            {subtitle ? <p className={css.subtitle}>{subtitle}</p> : null}
          </header>

          {tabs && tabs.length > 0 ? (
            <div className={css.tabs}>
              {tabs.map((tab, idx) =>
                tab.disabled ? (
                  <span
                    key={tab.linkProps.name + idx}
                    className={classNames(css.tab, css.tabDisabled)}
                    aria-disabled="true"
                  >
                    {tab.text}
                  </span>
                ) : (
                  <NamedLink
                    key={tab.linkProps.name + idx}
                    className={classNames(css.tab, { [css.tabActive]: tab.selected })}
                    {...tab.linkProps}
                  >
                    {tab.text}
                  </NamedLink>
                )
              )}
            </div>
          ) : null}

          <div className={css.tabContent}>{children}</div>
        </div>
      </DashboardWrapper>
    </Page>
  );
};

SettingsLayout.defaultProps = {
  className: null,
  rootClassName: null,
  subtitle: null,
  tabs: null,
  pageTitle: null,
  scrollingDisabled: false,
  orgId: null,
};

SettingsLayout.propTypes = {
  className: string,
  rootClassName: string,
  currentPage: string.isRequired,
  title: string.isRequired,
  subtitle: string,
  pageTitle: string,
  scrollingDisabled: bool,
  orgId: string,
  tabs: arrayOf(
    shape({
      text: node.isRequired,
      selected: bool,
      disabled: bool,
      linkProps: shape({ name: string.isRequired }).isRequired,
    })
  ),
  children: node,
};

export default SettingsLayout;
